import {
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterOperator,
  GridSingleSelectColDef,
} from '@mui/x-data-grid'
import {
  FilterCapabilityConfig,
  FilterFieldCapability,
  ScalarType,
} from '@omnea-app/packages/backend/filtering/types/baseFilterTypes'
import { getSupportedOperators } from '@omnea-app/packages/backend/filtering/types/supportedOperators'
import {
  ColumnCapabilities,
  createColumnCapabilities,
} from '@omnea-app/packages/backend/filtering/utils/createColumnCapabilities'
import { getDateFilterOperators } from '../../columns/definitions/getDateFilterOperators'

const DEFAULT_COLUMN_CAPABILITIES: Partial<ColumnCapabilities> = {
  groupable: false,
  aggregable: false,
  availableAggregationFunctions: [],
}

// MUI's singleSelect operators render the value dropdown but ship as `is`/`not`; rename them to the
// executor's equals/doesNotEqual so the dropdown survives while the operator names match the backend.
const SINGLE_SELECT_OPERATOR_RENAMES = { is: 'equals', not: 'doesNotEqual' } as const

export const getSingleSelectBackendOperators = (): GridFilterOperator[] =>
  getGridSingleSelectOperators().map((op) => {
    const renamed = SINGLE_SELECT_OPERATOR_RENAMES[op.value as keyof typeof SINGLE_SELECT_OPERATOR_RENAMES]
    return { ...op, value: renamed ?? op.value }
  })

// The MUI operators the Kysely executor actually serves for a field: the candidate operators for its
// value-input, kept to what getSupportedOperators reports (the same source the backend and the
// partial-mode gate use, so the offered set can't drift from what v2 runs). One rule for scalar,
// relation and computed columns.
const filterOperatorsForField = (fieldConfig: FilterFieldCapability): GridFilterOperator[] => {
  const served = getSupportedOperators(fieldConfig)
  const keepServed = (operators: GridFilterOperator[]) => operators.filter((op) => served.includes(op.value))

  if (fieldConfig.type !== 'scalar') return keepServed(getGridStringOperators()) // relation / computed

  switch (fieldConfig.scalarType) {
    case 'number':
      return keepServed(getGridNumericOperators())
    case 'date':
    case 'timestamp':
      return keepServed(getDateFilterOperators())
    case 'enum':
    case 'boolean': {
      // Value operators come from the singleSelect dropdown (renamed); isEmpty/isNotEmpty are value-less (string).
      const emptyOperators = getGridStringOperators().filter((op) => op.value === 'isEmpty' || op.value === 'isNotEmpty')
      return keepServed([...getSingleSelectBackendOperators(), ...emptyOperators])
    }
    default:
      return keepServed(getGridStringOperators()) // string / uuid / json
  }
}

// Thin wrapper for callers that only have a scalar type (custom-field fallback, documents).
export const getFilterOperatorsForScalarType = (scalarType: ScalarType): GridFilterOperator[] =>
  filterOperatorsForField({ type: 'scalar', scalarType })

// MUI seeds a new filter's operator from filterOperators[0]; partial mode keeps every operator
// selectable, so the v2-served ones must lead or the seeded default is one v2 can't serve.
export const orderOperatorsBySupport = (columns: GridColDef[], config: FilterCapabilityConfig): GridColDef[] =>
  columns.map((column) => {
    const fieldConfig = config.filterableFields[column.field]
    if (!fieldConfig || !column.filterOperators) return column

    const served = getSupportedOperators(fieldConfig)
    const supported = column.filterOperators.filter((op) => served.includes(op.value))
    if (supported.length === 0 || supported.length === column.filterOperators.length) return column

    const unsupported = column.filterOperators.filter((op) => !served.includes(op.value))
    return { ...column, filterOperators: [...supported, ...unsupported] }
  })

/**
 * Generic function to apply backend column capabilities to MUI DataGrid column definitions
 * This ensures frontend column properties match backend support
 *
 * @param columns - MUI DataGrid column definitions
 * @param capabilities - Backend column capabilities mapping
 * @returns Updated column definitions with capabilities applied
 */
export function applyBackendColumnCapabilities<T extends string>(
  columns: GridColDef[],
  config: FilterCapabilityConfig
): GridColDef[] {
  const columnCapabilities = createColumnCapabilities({
    filterConfig: config,
  })

  return columns.map((column) => {
    const capability = columnCapabilities[column.field as T]

    if (!capability) {
      const isCustomField = typeof column.field === 'string' && column.field.startsWith('customField|')

      if (isCustomField) {
        // Custom fields are filterable but not sortable (requires complex database joins)
        return {
          ...column,
          sortable: false,
          filterable: true,
          filterOperators: column.filterOperators || getFilterOperatorsForScalarType('string'),
          ...DEFAULT_COLUMN_CAPABILITIES,
        }
      }

      // If no capability config found for non-custom fields, then we can't filter/sort/group
      return {
        ...column,
        sortable: false,
        filterable: false,
        ...DEFAULT_COLUMN_CAPABILITIES,
      }
    }

    // Get the backend field configuration
    const fieldConfig = config.filterableFields[column.field]
    const baseColumn: GridColDef = {
      ...column,
      // Spread the defaults first so a per-field capability (e.g. groupable) survives instead of
      // being reset to the default below it.
      ...DEFAULT_COLUMN_CAPABILITIES,
      sortable: capability.sortable && column.sortable !== false,
      filterable: capability.filterable && column.filterable !== false,
      groupable: capability.groupable && column.groupable !== false,
    }

    // Apply filter operators and column type based on backend field type
    if (fieldConfig?.type === 'scalar' && fieldConfig.scalarType) {
      const filterOperators = filterOperatorsForField(fieldConfig)

      // Only apply backend operators if the column doesn't already have custom filter operators
      const filterOperatorsToApply = column.filterOperators ? undefined : filterOperators

      // Set MUI column type for enum fields (enables singleSelect filter UI).
      // Preserve caller-supplied valueOptions the same way we preserve filterOperators — a column
      // that presents a translated pill (e.g. the operational-status column) already carries the
      // right options and mustn't be overwritten by the raw backend enum keys.
      if (fieldConfig.scalarType === 'enum' && fieldConfig.enumMapping) {
        return {
          ...baseColumn,
          filterOperators: filterOperatorsToApply,
          type: 'singleSelect',
          valueOptions:
            (column as GridSingleSelectColDef).valueOptions ??
            Object.keys(fieldConfig.enumMapping).map((displayValue) => ({
              value: displayValue,
              label: displayValue,
            })),
        } as GridSingleSelectColDef
      }

      // Set MUI column type for boolean fields (display as True/False dropdown)
      if (fieldConfig.scalarType === 'boolean') {
        return {
          ...baseColumn,
          filterOperators: filterOperatorsToApply,
          type: 'singleSelect',
          valueOptions: [
            { value: 'True', label: 'True' },
            { value: 'False', label: 'False' },
          ],
        } as GridSingleSelectColDef
      }

      // Apply filter operators for non-enum/boolean fields
      if (filterOperatorsToApply) {
        return {
          ...baseColumn,
          filterOperators: filterOperatorsToApply,
        }
      }
    }

    // Relation and computed fields use the string-style filter UI, restricted to the operators the
    // executor serves (e.g. contains-only for requests createdBy/assignees) — offering more would let
    // the grid send an operator v2 silently drops.
    if ((fieldConfig?.type === 'relation' || fieldConfig?.type === 'computed') && !column.filterOperators) {
      return { ...baseColumn, filterOperators: filterOperatorsForField(fieldConfig) }
    }

    return baseColumn
  })
}
